import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
  loadAttentionChallenge,
  verifyAttention,
  attentionScreenoutFlow,
} from '../../data/actions/appActions';
import '../../styles/AttentionGate.scss';

function AttentionGate(props) {
  const {
    attentionChallenge,
    attentionVerified,
    attentionAttemptsLeft,
    attentionScreenout,
    loadChallenge,
    submitAnswer,
    runScreenout,
    children,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasFailedAttempt, setHasFailedAttempt] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (attentionVerified) return;
    if (attentionScreenout) return;
    if (!attentionChallenge) {
      loadChallenge();
    }
  }, [attentionChallenge, attentionVerified, attentionScreenout, loadChallenge]);

  useEffect(() => {
    if (!attentionScreenout) return;
    runScreenout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attentionScreenout]); // runScreenout intentionally omitted — stable dispatch wrapper not needed, fire once on flag flip

  // Reset input when challenge changes
  const challengeId = attentionChallenge && attentionChallenge.id;
  useEffect(() => {
    setInputValue('');
    setSubmitting(false);
  }, [challengeId]);

  // Track that the attempts dropped below 3 (failed at least once)
  useEffect(() => {
    if (typeof attentionAttemptsLeft === 'number' && attentionAttemptsLeft < 2) {
      setHasFailedAttempt(true);
    }
  }, [attentionAttemptsLeft]);

  if (attentionVerified) {
    return children || null;
  }

  if (attentionScreenout) {
    return (
      <div className="attentionGate">
        <div className="attentionGate__inner">
          <p className="attentionGate__copy">
            Vi behöver skydda kvaliteten i undersökningen. Tack för att du
            försökte!
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (value) => {
    // Sync ref guards against rapid double-tap on pick buttons; React state
    // updates are async so two clicks within the same tick both see
    // submitting === false.
    if (submittingRef.current) return;
    const trimmed = (value || '').toString().trim();
    if (!trimmed) return;
    submittingRef.current = true;
    setSubmitting(true);
    setNetworkError(false);
    const result = await submitAnswer(trimmed);
    if (result === 'network_error') {
      setNetworkError(true);
    }
    submittingRef.current = false;
    setSubmitting(false);
  };

  const renderBody = () => {
    if (!attentionChallenge) {
      return <p className="attentionGate__loading">Laddar…</p>;
    }

    const { type, question, options } = attentionChallenge;

    if (type === 'pick' && Array.isArray(options) && options.length > 0) {
      return (
        <>
          <p className="attentionGate__question">{question}</p>
          <div className="attentionGate__options">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                className="attentionGate__optionBtn"
                onClick={() => handleSubmit(opt)}
                disabled={submitting}
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      );
    }

    return (
      <>
        <p className="attentionGate__question">{question}</p>
        <form
          className="attentionGate__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(inputValue);
          }}
        >
          <input
            className="attentionGate__input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
            autoComplete="off"
            disabled={submitting}
          />
          <button
            type="submit"
            className="attentionGate__submitBtn"
            disabled={submitting || !inputValue.trim()}
          >
            Skicka
          </button>
        </form>
      </>
    );
  };

  return (
    <>
      <div className="attentionGate">
        <div className="attentionGate__inner">
          <h2 className="attentionGate__title">
            En snabb kontroll innan vi börjar
          </h2>
          {renderBody()}
          {networkError && (
            <p className="attentionGate__error">
              Tekniskt fel — försök igen.
            </p>
          )}
          {!networkError &&
            hasFailedAttempt &&
            typeof attentionAttemptsLeft === 'number' &&
            attentionAttemptsLeft > 0 && (
              <p className="attentionGate__error">
                Fel svar — försök igen ({attentionAttemptsLeft} försök kvar)
              </p>
            )}
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  attentionChallenge: state.app.attentionChallenge,
  attentionVerified: state.app.attentionVerified,
  attentionAttemptsLeft: state.app.attentionAttemptsLeft,
  attentionScreenout: state.app.attentionScreenout,
});

const mapDispatchToProps = (dispatch) => ({
  loadChallenge: () => dispatch(loadAttentionChallenge()),
  submitAnswer: (answer) => dispatch(verifyAttention(answer)),
  runScreenout: () => dispatch(attentionScreenoutFlow()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AttentionGate);
